import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { Typeahead, AsyncTypeahead } from "react-bootstrap-typeahead";
import {
  Form,
  FloatingLabel,
  Image,
  OverlayTrigger,
  Tooltip
} from "react-bootstrap";
import { formatDate } from "../commons/FormatValue";
import { AngleLeftIcon, SearchIcon } from "../data/iconImage";
import { filteredIndex, filteredItemInArray } from "../utils/ManageArray";
import debounce from "lodash/debounce";
import "react-bootstrap-typeahead/css/Typeahead.scss";
import "react-bootstrap-typeahead/css/Typeahead.bs5.scss";

const FormControlComponent = ({
  as,
  min,
  max,
  type,
  name,
  rows,
  label,
  pattern,
  required,
  readOnly,
  disabled,
  className,
  placeholder,
  feedbackMessage,
  placement = "bottom-start",
  onChange,
  value,
  style
}) => {
  const tooltip = value ? (
    <Tooltip id={`tooltip-${name}`}>
      {type === "date" ? formatDate(value) : value}
    </Tooltip>
  ) : (
    <></>
  );

  return (
    <>
      {label && (
        <Form.Label
          className={`fs-4 ${className?.label || ""}`}
          style={style?.label}
        >
          {label}
        </Form.Label>
      )}
      <OverlayTrigger placement={placement} overlay={tooltip}>
        <Form.Control
          as={as}
          min={min || null}
          max={max || null}
          rows={rows || null}
          type={type || null}
          name={name || null}
          pattern={pattern || null}
          required={required || null}
          readOnly={readOnly || null}
          disabled={disabled || null}
          placeholder={placeholder || null}
          className={`p-3 fs-4 ${className?.input || ""}`}
          onChange={onChange || null}
          style={style?.input}
          value={value || ""}
        />
      </OverlayTrigger>
      {pattern && (
        <Form.Control.Feedback tooltip type="invalid" className="text-start">
          {feedbackMessage}
        </Form.Control.Feedback>
      )}
    </>
  );
};

FormControlComponent.propTypes = {
  as: PropTypes.string,
  type: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.string,
  rows: PropTypes.number,
  pattern: PropTypes.string,
  required: PropTypes.bool,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.object,
  placement: PropTypes.string,
  placeholder: PropTypes.string,
  feedbackMessage: PropTypes.string,
  onChange: PropTypes.func,
  style: PropTypes.object,
  min: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  max: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  center: PropTypes.bool
};

const FloatingControlComponent = ({
  min,
  max,
  type,
  name,
  label,
  pattern,
  required,
  readOnly,
  disabled,
  className,
  placeholder,
  feedbackMessage,
  placement = "bottom-start",
  onChange,
  value,
  style,
  center
}) => {
  const tooltip = value ? (
    <Tooltip id={`tooltip-${name}`}>
      {type === "date" ? formatDate(value) : value}
    </Tooltip>
  ) : (
    <></>
  );

  return (
    <OverlayTrigger placement={placement} overlay={tooltip}>
      <FloatingLabel
        label={label}
        controlId={name}
        className={`fs-5 ${className?.label || ""}`}
        style={style?.label}
      >
        <Form.Control
          min={min || null}
          max={max || null}
          type={type || null}
          name={name || null}
          pattern={pattern || null}
          required={required || null}
          readOnly={readOnly || null}
          disabled={disabled || null}
          placeholder={placeholder || null}
          className={`p-3 fs-4 ${className?.input || ""} ${center ? 'text-center' : ''}  `}
          onChange={onChange || null}
          style={style?.input || null}
          value={value || ""}
        />
        {pattern && (
          <Form.Control.Feedback tooltip type="invalid" className="text-start">
            {feedbackMessage}
          </Form.Control.Feedback>
        )}
      </FloatingLabel>
    </OverlayTrigger>
  );
};

FloatingControlComponent.propTypes = {
  type: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.string,
  pattern: PropTypes.string,
  required: PropTypes.bool,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.object,
  placement: PropTypes.string,
  placeholder: PropTypes.string,
  feedbackMessage: PropTypes.string,
  onChange: PropTypes.func,
  style: PropTypes.object,
  min: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  max: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  center: PropTypes?.bool
};

const FormCheckComponent = ({
  id,
  type,
  name,
  label,
  image,
  inline,
  required,
  readOnly,
  disabled,
  className,
  onChange,
  checked,
  style
}) => {
  return (
    <Form.Check
      inline={inline}
      className={className?.check}
      style={style?.check}
    >
      <Form.Check.Input
        id={id}
        type={type}
        name={name}
        required={required}
        readOnly={readOnly}
        disabled={disabled}
        className={className?.input || ""}
        onChange={onChange}
        checked={checked}
        style={style?.input}
      />
      {(label || image) && (
        <Form.Check.Label
          htmlFor={id}
          className={`fs-4 ${className?.label || ""}`}
          style={style?.label}
        >
          {label && label}
          {image && image}
        </Form.Check.Label>
      )}
    </Form.Check>
  );
};

FormCheckComponent.propTypes = {
  id: PropTypes.string,
  inline: PropTypes.bool,
  type: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.string,
  image: PropTypes.element,
  required: PropTypes.bool,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.object,
  onChange: PropTypes.func,
  checked: PropTypes.bool,
  style: PropTypes.object
};

const SearchSelectComponent = ({
  labelField,
  floatingClass,
  filterBy,
  options,
  queryService,
  onChange,
  value,
  placeholder,
  isInvalid,
  disabled,
  noneAsync,
  minKeywordLength,
  onChangeInput,
  hideTooltip
}) => {
  const [option, setOption] = useState([]);
  const [selected, setSelected] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const tooltip = (hideTooltip ? !hideTooltip : selected) ? (
    <Tooltip id={`tooltip-${labelField}`}>{selected?.label}</Tooltip>
  ) : (
    <></>
  );

  const typeaheadProps = {
    disabled,
    isLoading,
    isInvalid,
    placeholder,
    id: labelField,
    clearButton: true,
    emptyLabel: "ไม่พบข้อมูล",
    searchText: "กำลังค้นหา...",
    promptText: "พิมพ์เพื่อค้นหา...",
    labelKey: ({ label }) => label,
    selected:
      selected && `${selected?.value}` === `${value}`
        ? [selected]
        : filteredItemInArray(option, "value", value),
    onChange: (selected) => {
      // console.log("🚀 ~ selected:", selected)
      setSelected(selected[0] || null);
      onChange(selected[0] || null);
    },

    flip: true,
  };

  const debouncedSearch = debounce(async (query) => {
    // console.log("🚀 ~ debouncedSearch ~ query:", query)
    setIsLoading(true);

    const filteredOptions = queryService
      ? await queryService(`?limit=1000&keyword=${query}`, query)
      : options.filter(({ value, label }) =>
        [value, label].some((str) =>
          str?.toLowerCase().includes(query?.toLowerCase())
        )
      );

    setIsLoading(false);
    setOption(filteredIndex(filteredOptions, "value") || []);
    return filteredOptions;
  }, 300);

  useEffect(() => {
    setOption(filteredIndex(options, "value"));
    if (!value) setSelected(null);
  }, [queryService, options]);

  return (
    <OverlayTrigger placement="bottom-start" overlay={tooltip}>
      <FloatingLabel
        label={labelField}
      >
        {noneAsync ? (
          <Typeahead {...typeaheadProps} options={option} onInputChange={(e) => onChangeInput && onChangeInput(e)} />
        ) : (
          <AsyncTypeahead
            {...typeaheadProps}
            options={option}
            minLength={minKeywordLength || 3}
            filterBy={filterBy || (() => true)}
            onSearch={debouncedSearch}
          />
        )}
        <span
          className="p-2 position-absolute top-50 start-0 translate-middle-y"
          style={{ width: "48px", height: "48px" }}
        >
          <Image className="p-1" src={SearchIcon} alt="Search Icon" />
        </span>
        <span
          className="p-2 position-absolute top-50 end-0 translate-middle-y"
          style={{ width: "48px", height: "48px" }}
        >
          <Image className="p-1" src={AngleLeftIcon} alt="AngleDown Icon" />
        </span>
      </FloatingLabel>
    </OverlayTrigger>
  );
};

SearchSelectComponent.propTypes = {
  labelField: PropTypes.string.isRequired,
  floatingClass: PropTypes.string,
  filterBy: PropTypes.array,
  options: PropTypes.array,
  queryService: PropTypes.func,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  isInvalid: PropTypes.bool,
  disabled: PropTypes.bool,
  noneAsync: PropTypes.bool,
  minKeywordLength: PropTypes.number,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool
  ]),
  onChangeInput: PropTypes.func,
  hideTooltip: PropTypes.bool
};

export {
  FormControlComponent,
  FloatingControlComponent,
  FormCheckComponent,
  SearchSelectComponent
};
